import type { Language } from '@eformulieren/engine'
import { laagOf } from '@eformulieren/engine'
import { type Field, type Person, type Projection, type ProjectionSection, type Thread, newId } from '@eformulieren/model'
import { type ReactNode, useCallback, useMemo, useState } from 'react'
import type * as Y from 'yjs'
import type { CitationField } from '../citation/collectClusters.js'
import { type CitationPlace, citationPlaces, editSourceInLayer, layerLibrary, removeSourceInLayer } from '../citation/layerSources.js'
import { SourcesOverview, type SourcesLayer } from '../citation/SourcesOverview.js'
import { CommentsOverview, type OverviewGroup } from '../comments/CommentsOverview.js'
import { anchoredInDoc } from '../comments/docAnchors.js'
import { outstandingCount } from '../comments/useThreads.js'
import { addMessage, resolveThread } from '../document/sectionDocument.js'
import { CommentDeleteRefused, deleteMessage, deleteThread } from '../document/deleteComment.js'
import { inLanguage } from '../language/text.js'
import type { AdviceTarget } from './adviceModel.js'
import type { CommentDeleteRequest, CommentRequest } from './FillForm.js'
import type { SidePanelContext } from './Form.js'
import { type FormPanel, type FormPanelId, type FormPanels, PanelButtons } from './RightPanel.js'

/**
 * De panelen Bronnen en Opmerkingen voor `FillForm`, en de knoppen die ze openen (R-50, R-73, ontwerp A
 * van het invulscherm). Geschiedenis komt van buiten mee (`extra`), want dat paneel heeft een bron van
 * versies nodig die het voorbeeld van de configurator niet heeft.
 *
 * Een eigen bestand om `FillForm` klein te houden: wat hier staat leest alleen wat `FillForm` al heeft
 * (de documenten, de draadjes per sectie uit één abonnement, de citatiescopes en de regels voor een
 * opmerking) en voegt geen tweede abonnement toe.
 */

export interface FormPanelsInput {
  readonly projectie: Pick<Projection, 'secties'>
  readonly documenten: ReadonlyMap<string, Y.Doc>
  readonly taal: Language
  /** De zichtbare secties: een draadje in een verborgen sectie staat niet in het paneel, want springen kan er niet heen. */
  readonly zichtbaar: ReadonlySet<string>
  readonly dradenPer: Readonly<Record<string, readonly Thread[]>>
  /** Per laag de secties met hun rich-text-velden, zoals de citatieberekening ze gebruikt. */
  readonly citatieScopes: readonly (readonly { readonly sectieId: string; readonly fieldIds: readonly CitationField[] }[])[]
  readonly kanSchrijven: (sectionId: string) => boolean
  readonly opmerkingWeg: (section: ProjectionSection) => 'document' | 'pod' | undefined
  readonly onOpmerking?: (request: CommentRequest) => Promise<{ readonly draad: string }>
  /** Een eigen opmerking verwijderen langs de pod, voor een sectie die de lezer alleen leest (eisenboek 12.45). */
  readonly onOpmerkingVerwijderen?: (request: CommentDeleteRequest) => Promise<unknown>
  /** Wie dit leest, zoals de pod hem stempelt: aan wie een bericht toebehoort. */
  readonly ik?: Person
  readonly naamVan: (person: string) => string
  /** Panelen die er nog bij komen, na Bronnen en Opmerkingen: Geschiedenis. */
  readonly extra?: readonly FormPanel[]
}

export interface FormPanelsOutput {
  readonly panelen: FormPanels | undefined
  /** De knoppen rechts naast de titel. */
  readonly kopKnoppen: ReactNode
  /** Dezelfde knoppen, op een telefoon achter de knop Meer in de kopbalk. */
  readonly meerKnoppen: ReactNode
  /** De namen voor de schermlezertekst van de knop Meer: "bronnen", "opmerkingen". */
  readonly meerNamen: readonly string[]
  /** Een paneel openen van buiten, bijvoorbeeld Geschiedenis na het kiezen van een stand. */
  readonly open: (id: FormPanelId) => void
  readonly actief: FormPanelId | undefined
}

/** Het label van een veld op zijn id, ook in een rij van een herhaalgroep. */
function fieldLabel(fields: readonly Field[], fieldId: string, language: Language): string | undefined {
  for (const field of fields) {
    if (field.id === fieldId) return field.label === undefined ? undefined : inLanguage(field.label, language)
    if (field.control === 'herhaalgroep') {
      const inner = fieldLabel(field.rijVelden, fieldId, language)
      if (inner !== undefined) return inner
    }
  }
  return undefined
}

/** Na een sprong de plek in de tekst in beeld en even gemarkeerd, als de editor haar heeft getekend. */
function markInText(sectionId: string, selector: string): void {
  requestAnimationFrame(() => {
    const element = document.getElementById(sectionId)?.querySelector<HTMLElement>(selector)
    if (element === null || element === undefined) return
    const soft = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    element.scrollIntoView({ behavior: soft ? 'smooth' : 'auto', block: 'center' })
    element.setAttribute('data-gemarkeerd', 'ja')
    window.setTimeout(() => element.removeAttribute('data-gemarkeerd'), 2000)
  })
}

/**
 * Waar een draadje hangt, voor "Bij …" in de kaart. Een draadje met een citaat hangt aan tekst in het
 * veld en heet naar het veld; een draadje van vóór besluit 12.41 zonder citaat hangt aan het veld of
 * de sectie als geheel, en zegt dat ook.
 */
function placeOf(thread: Thread, section: ProjectionSection, title: string, language: Language): string {
  if (thread.veldId === undefined) return `${language === 'nl' ? 'de sectie' : 'the section'} ${title}`
  const label = fieldLabel(section.velden, thread.veldId, language) ?? (language === 'nl' ? 'deze vraag' : 'this question')
  if (thread.citaat !== '') return label
  return `${language === 'nl' ? 'het veld' : 'the field'} ${label}`
}

const SOURCES: Readonly<Record<Language, string>> = { nl: 'Bronnen', en: 'Sources' }
const COMMENTS: Readonly<Record<Language, string>> = { nl: 'Opmerkingen', en: 'Comments' }

export function useFormPanels(input: FormPanelsInput): FormPanelsOutput {
  const { projectie: projection, documenten: documents, taal: language, zichtbaar: visible, dradenPer: threadsPer, citatieScopes: scopes, kanSchrijven: canWrite, opmerkingWeg: commentWay, onOpmerking: postComment, onOpmerkingVerwijderen: deleteComment, ik: me, naamVan: nameOf, extra = [] } = input
  const [active, setActive] = useState<FormPanelId | undefined>(undefined)
  const [show, setShow] = useState(0)
  // Een wijziging van een bron hertekent niet vanzelf; deze teller wel.
  const [, setRevision] = useState(0)

  const open = useCallback((id: FormPanelId) => {
    setActive(id)
    setShow((count) => count + 1)
  }, [])
  const close = useCallback((options?: { readonly focusTerug?: boolean }) => {
    setActive((current) => {
      if (current !== undefined && options?.focusTerug !== false) {
        // De focus terug naar de knop die het paneel opende, de zichtbare van de twee (WCAG 2.4.3).
        requestAnimationFrame(() => {
          const buttons = document.querySelectorAll<HTMLElement>(`[data-paneelknop="${current}"]`)
          for (const button of buttons) if (button.offsetParent !== null) return button.focus()
        })
      }
      return undefined
    })
  }, [])
  const toggle = useCallback((id: FormPanelId) => (active === id ? close() : open(id)), [active, close, open])

  const sectionsById = useMemo(() => new Map(projection.secties.map((section) => [section.id, section])), [projection])

  // Bronnen: een bibliotheek per laag, alleen als de laag een rich-text-veld heeft.
  const sourceScopes = useMemo(() => scopes.filter((scope) => scope.some((section) => section.fieldIds.length > 0)), [scopes])
  /**
   * De lagen met hun bibliotheek, en pas als het paneel er staat ook de plekken in de tekst: die vragen
   * een wandeling door elk rich-text-fragment, en dat hoort niet bij elke toetsaanslag (de knop heeft
   * alleen het aantal bronnen nodig).
   */
  const layersWith = (places: boolean): readonly (SourcesLayer & { readonly scope: readonly string[] })[] =>
    sourceScopes.map((scope) => {
      const ids = scope.map((section) => section.sectieId)
      const first = sectionsById.get(ids[0] ?? '')
      const layer = first === undefined ? 'invoer' : laagOf(first.type) === 'toets' ? 'toets' : 'invoer'
      return {
        laag: layer,
        scope: ids,
        bronnen: [...layerLibrary(documents, ids).values()],
        plaatsen: places ? citationPlaces(documents, scope) : new Map(),
        beheren: ids.some(canWrite),
      }
    })
  const sourceCount = layersWith(false).reduce((sum, layer) => sum + layer.bronnen.length, 0)

  const placeName = (place: CitationPlace): string => {
    const section = sectionsById.get(place.sectieId)
    const label = section === undefined ? undefined : fieldLabel(section.velden, place.veldId, language)
    const title = section === undefined ? '' : inLanguage(section.titel, language)
    return label === undefined ? title : `${label} in ${title}`
  }

  const sourcesPanel: FormPanel | undefined =
    sourceScopes.length === 0
      ? undefined
      : {
          id: 'bronnen',
          titel: SOURCES[language],
          knop: SOURCES[language],
          teller: { aantal: sourceCount, woorden: String(sourceCount) },
          render: (context: SidePanelContext) => {
            const layers = layersWith(true)
            return (
            <SourcesOverview
              taal={language}
              lagen={layers}
              plaatsNaam={placeName}
              onSpring={(place) => {
                context.springNaar({ sectieId: place.sectieId, veldId: place.veldId })
                markInText(place.sectieId, `[data-referentie="${CSS.escape(place.citatieId)}"]`)
              }}
              onWijzig={(layer, sourceId, changed) => {
                const found = layers.find((candidate) => candidate.laag === layer)
                const outcome = found === undefined ? { buitenBereik: false } : editSourceInLayer(documents, found.scope, canWrite, sourceId, changed)
                setRevision((count) => count + 1)
                return outcome
              }}
              onVerwijder={(layer, sourceId) => {
                const found = layers.find((candidate) => candidate.laag === layer)
                if (found === undefined) return false
                const inUse = new Set([...found.plaatsen.keys()])
                const removed = removeSourceInLayer(documents, found.scope, canWrite, sourceId, inUse)
                setRevision((count) => count + 1)
                return removed
              }}
            />
            )
          },
        }

  // Opmerkingen: per zichtbare sectie in formuliervolgorde.
  const richtextFields = useMemo(() => new Map(scopes.flat().map((section) => [section.sectieId, section.fieldIds])), [scopes])
  const groups: OverviewGroup[] = projection.secties.flatMap((section) => {
    if (!visible.has(section.id)) return []
    const threads = threadsPer[section.id] ?? []
    if (threads.length === 0) return []
    const way = commentWay(section)
    const doc = documents.get(section.id)
    const title = inLanguage(section.titel, language)
    return [
      {
        sectieId: section.id,
        titel: title,
        draden: threads.map((thread) => ({
          draad: thread,
          plek: placeOf(thread, section, title, language),
          // Pas bij het tekenen van het paneel, zie `withAnchors`.
          verankerd: false,
          bewerkbaar: way === 'document' && doc !== undefined,
          kanReageren: way === 'document' ? doc !== undefined : way === 'pod',
        })),
      },
    ]
  })
  /** Of het anker van een draadje met citaat nog in de tekst staat; alleen als het paneel er staat. */
  const withAnchors = (list: readonly OverviewGroup[]): readonly OverviewGroup[] =>
    list.map((group) => {
      const doc = documents.get(group.sectieId)
      const anchored = doc === undefined || !group.draden.some((entry) => entry.draad.citaat !== '') ? new Set<string>() : anchoredInDoc(doc, richtextFields.get(group.sectieId) ?? [])
      return { ...group, draden: group.draden.map((entry) => ({ ...entry, verankerd: anchored.has(entry.draad.id) })) }
    })
  // Een opmerking begint bij geselecteerde tekst, dus alleen in een sectie met een rich-text-veld.
  const anyCommentPlace = projection.secties.some((section) => commentWay(section) !== undefined && (richtextFields.get(section.id)?.length ?? 0) > 0)
  const openComments = groups.reduce((sum, group) => sum + outstandingCount(group.draden.map((entry) => entry.draad)), 0)

  const jumpToThread = (context: SidePanelContext, sectionId: string, thread: Thread): void => {
    const target: AdviceTarget = thread.veldId === undefined ? { sectieId: sectionId } : { sectieId: sectionId, veldId: thread.veldId }
    context.springNaar(target)
    if (thread.citaat !== '') markInText(sectionId, `[data-draad-id="${CSS.escape(thread.id)}"]`)
  }

  const commentsPanel: FormPanel | undefined =
    groups.length === 0 && !anyCommentPlace
      ? undefined
      : {
          id: 'opmerkingen',
          titel: COMMENTS[language],
          knop: COMMENTS[language],
          teller: { aantal: openComments, woorden: `${String(openComments)} open` },
          render: (context: SidePanelContext) => (
            <CommentsOverview
              taal={language}
              groepen={withAnchors(groups)}
              naamVan={nameOf}
              kanPlaatsen={anyCommentPlace}
              onSpring={(sectionId, thread) => jumpToThread(context, sectionId, thread)}
              onAntwoord={(sectionId, threadId, text) => {
                const section = sectionsById.get(sectionId)
                const way = section === undefined ? undefined : commentWay(section)
                const doc = documents.get(sectionId)
                if (way === 'document' && doc !== undefined) {
                  addMessage(doc, threadId, { id: newId('bericht'), tekst: text })
                  return undefined
                }
                if (way === 'pod') return postComment?.({ sectie: sectionId, draad: threadId, tekst: text })
                return undefined
              }}
              onAfdoen={(sectionId, threadId, settled) => {
                const section = sectionsById.get(sectionId)
                const doc = documents.get(sectionId)
                if (section !== undefined && commentWay(section) === 'document' && doc !== undefined) resolveThread(doc, threadId, settled)
              }}
              {...(me === undefined ? {} : { ik: me })}
              onVerwijder={(sectionId, threadId, messageId) => {
                // Dezelfde twee wegen als reageren: wie schrijft, in het document; een meelezer langs de pod.
                const section = sectionsById.get(sectionId)
                const way = section === undefined ? undefined : commentWay(section)
                const doc = documents.get(sectionId)
                if (way === 'document' && doc !== undefined && me !== undefined) {
                  try {
                    if (messageId === undefined) deleteThread(doc, threadId, me)
                    else deleteMessage(doc, threadId, messageId, me)
                  } catch (error) {
                    // Intussen al weg, of toch niet van deze lezer: dan verandert er niets, en de kaart toont wat er staat.
                    if (!(error instanceof CommentDeleteRefused)) throw error
                  }
                  return undefined
                }
                if (way === 'pod') return deleteComment?.({ sectie: sectionId, draad: threadId, ...(messageId === undefined ? {} : { bericht: messageId }) })
                return undefined
              }}
            />
          ),
        }

  const panels = [sourcesPanel, commentsPanel, ...extra].filter((panel): panel is FormPanel => panel !== undefined)
  // Een paneel dat er niet meer is (de laatste opmerking in een sectie die verborgen raakte) is dicht.
  const shownActive = panels.some((panel) => panel.id === active) ? active : undefined
  const names = panels.map((panel) => panel.titel.toLowerCase())

  return {
    panelen: panels.length === 0 ? undefined : { panelen: panels, actief: shownActive, toon: show, onSluit: close },
    kopKnoppen: <PanelButtons panelen={panels} actief={shownActive} taal={language} plek="kop" onKies={toggle} />,
    meerKnoppen: <PanelButtons panelen={panels} actief={shownActive} taal={language} plek="meer" onKies={toggle} />,
    meerNamen: names,
    open,
    actief: shownActive,
  }
}
